import { getScreenFromUrl } from './getScreenFromUrl';

// Marketing attributes to extract from the URL from which the marketing event is fired.
// List compiled by Atlassian Marketing Engineering here:
// https://hello.atlassian.net/wiki/spaces/GTMDE/pages/1357221110/Analysis+Trello+hybrid+pages+and+UGC
const StaticAttributes = [
  // utm fields
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  // marketing identifiers
  'fbclid',
  'gclid',
  'gclsrc',
  'msclkid',
  // marketing fields
  'adposition',
  'adgroup',
  'campaign',
  'creative',
  'device',
  'keyword',
  'matchtype',
  'network',
  'placement',
];

// Attributes to extract from the url if they match the pattern
const DynamicAttributePatterns = [/\w+id/, /ds_\w+/, /dcm_\w+/];
const isParamMarketingAttribute = (param: string): boolean => {
  return DynamicAttributePatterns.some((pattern) => {
    return pattern.test(param);
  });
};

const extractSearchParamsAttributes = (url: URL): Record<string, string> => {
  const searchParams = url.searchParams;
  const attributes: Record<string, string> = {};
  for (const [key, value] of searchParams) {
    if (
      // Look for exact + pattern matches
      (StaticAttributes.includes(key) || isParamMarketingAttribute(key)) &&
      value
    ) {
      attributes[key] = value;
    }
  }
  return attributes;
};

//we want to process referrer urls differently than external urls or blog.trello.com
const checkReferrerForTrello = (referrer: string): boolean => {
  return referrer === 'trello.com' || referrer === 'trellis.coffee';
};

//gets the screenname for a referrer trello url so we can filter trello pathnames
const getReferrerPathName = (referrer: URL): string => {
  const screenName = getScreenFromUrl({ url: referrer });
  if (referrer.pathname !== '/') {
    return `/${screenName}`;
  }
  return '/';
};
interface ReferrerAttributes {
  page_referrer_url: string;
  page_referrer_path: string;
  page_referrer_domain: string;
  page_referrer_subdomain: string;
  page_referrer_url_params_json?: string;
}
const extractReferrerAttributes = (
  referrer: string,
): ReferrerAttributes | undefined => {
  // Exit if no valid referrer
  // Always a string, see: https://developer.mozilla.org/en-US/docs/Web/API/Document/referrer#value
  if (referrer === '') {
    return;
  }

  const referrerUrl = new URL(referrer);
  const isTrello = checkReferrerForTrello(referrerUrl.hostname);
  const attributes: ReferrerAttributes = {
    page_referrer_url: isTrello
      ? referrerUrl.origin + getReferrerPathName(referrerUrl)
      : referrerUrl.origin + referrerUrl.pathname,
    page_referrer_domain: referrerUrl.origin,
    page_referrer_subdomain: referrerUrl.origin, // same as domain, will be transformed in MarketingEng pipeline
    page_referrer_path: isTrello
      ? getReferrerPathName(referrerUrl)
      : referrerUrl.pathname,
  };

  // Get relevant marketing attributes from the referrer url
  if (referrerUrl.searchParams.toString().length > 0) {
    const referrerSearchParams = extractSearchParamsAttributes(referrerUrl);
    attributes.page_referrer_url_params_json = JSON.stringify(
      referrerSearchParams,
    );
    const referrerSearchParamsString = new URLSearchParams(
      referrerSearchParams,
    ).toString();
    attributes.page_referrer_url = `${attributes.page_referrer_url}?${referrerSearchParamsString}`;
  }

  return attributes;
};

interface BaseAttributes {
  writeKey: string;
  isMarketingEvent: boolean;
  page_path: string;
  page_domain: string;
  page_canonical_url: string;
  page_url: string;
  page_url_parameters?: string;
  page_url_params_json?: string;
}
export type MarketingAttributes = BaseAttributes | ReferrerAttributes;

/**
 * Returns marketing attributes for URLs without PII or UGC on Trello hybrid marketing/product pages. Use in GAS
 * analytics events when `{isMarketingEvent: true}` exists in the attributes : {...}.
 *
 * @example
 * //returns marketing attributes as an object to include with existing attributes.
 * const marketingAttributes = getMarketingAttributes('boardScreen');
 *
 * Analytics.sendScreenEvent({
 *   name: 'boardScreen',
 *   attributes: {
 *     ...marketingAttributes,
 *   },
 * });
 *
 */
export const getMarketingAttributes = (
  screenName: string,
  url = window.location.href,
  referrer = document.referrer,
): MarketingAttributes => {
  const urlObject = new URL(url);

  const baseAttributes: BaseAttributes = {
    writeKey: 'trello',
    isMarketingEvent: true,
    page_path: `/${screenName}`,
    page_domain: urlObject.origin,
    page_canonical_url: `${urlObject.origin}/${screenName}`,
    page_url: `${urlObject.origin}/${screenName}`,
  };

  const urlSearchParams = extractSearchParamsAttributes(urlObject);
  const urlSearchParamsString = new URLSearchParams(urlSearchParams).toString();
  if (urlSearchParamsString.length > 0) {
    baseAttributes.page_canonical_url += `?${urlSearchParamsString}`;
    baseAttributes.page_url += `?${urlSearchParamsString}`;
    baseAttributes.page_url_parameters = `?${urlSearchParamsString}`;
    baseAttributes.page_url_params_json = JSON.stringify(urlSearchParams);
  }

  return {
    ...baseAttributes,
    ...urlSearchParams,
    ...extractReferrerAttributes(referrer),
  };
};
